/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import memoizeLast from 'transmute/memoizeLast';
import compose from 'transmute/compose';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
// @ts-expect-error module not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { CALYPSO_LIGHT } from 'HubStyleTokens/colors';
import emptyFunction from 'react-utils/emptyFunction';

// @ts-expect-error module not typed
import CalleeErrorBoundary from './callee-error/CalleeErrorBoundary';
import CRMEmbeddedMessage from './crm-embedded/components/CRMEmbeddedMessage';
import NoCalleesPrompt from './callee-numbers/components/NoCalleesPrompt';
import CallSettingsNumbers from './callee-numbers/components/CallSettingsNumbers';
// @ts-expect-error module not typed
import EnsureCallingAccess from './calling-appeals/components/EnsureCallingAccess';
import { getData, isSucceeded } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallableObjects, getPhoneNumberProperties, getHasAssociatedObjects, getCallablePhoneNumberProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getCallableObjectAndAssociations } from 'calling-lifecycle-internal/callee-omnibus/operators/calleeOmnibusResultGetters';
import CallingOnboarding from './callee-numbers/components/CallingOnboarding';
// @ts-expect-error module not typed
import withCalleeOmnibus from './callee-omnibus/hoc/withCalleeOmnibus';
import withCallingAccess from './calling-appeals/hoc/withCallingAccess';
import withWidgetState from './widget-state/hoc/withWidgetState';
// @ts-expect-error module not typed
import withAccessLevelPrefetch from './access-level/hoc/withAccessLevelPrefetch';
import { getIsCRMEmbedded } from './utils/getIsCRMEmbedded';
import { READY, UNINITIALIZED, LOADING } from 'calling-internal-common/widget-status/constants/CallWidgetStates';
import preloadCallingWidget from 'calling-global-api/methods/preloadCallingWidget';
import updateCallingReadyState from 'calling-global-api/methods/updateCallingReadyState';
import getIn from 'transmute/getIn';
import { getCallProvidersFromOmnibusResult } from './callee-omnibus/operators/settingsOmnibusResultGetters';
import { getIsUngatedForBidirectionalProviderSwitching } from './calling-gates/utils/getIsUngatedForBidirectionalProviderSwitching';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "CallNumbersPopover__Wrapper",
  componentId: "oaphu8-0"
})(["max-width:615px;"]);
const Options = styled.div.withConfig({
  displayName: "CallNumbersPopover__Options",
  componentId: "oaphu8-1"
})([".option-item:hover{background-color:", ";}"], CALYPSO_LIGHT);
const CallNumbersPopover = props => {
  const hasCallableRecords = memoizeLast(calleesData => {
    const hasAssociatedObjects = getHasAssociatedObjects(calleesData);
    if (hasAssociatedObjects) {
      return true;
    }
    const callableObjects = getCallableObjects(calleesData);
    return callableObjects && callableObjects.some(callableObject => {
      const phoneNumberProperties = getPhoneNumberProperties(callableObject);
      return getCallablePhoneNumberProperties(phoneNumberProperties).size > 0;
    });
  });
  const renderCRMEmbeddedMessage = () => {
    const {
      subjectId,
      objectTypeId
    } = props;
    return /*#__PURE__*/_jsx(CRMEmbeddedMessage, {
      subjectId: subjectId,
      objectTypeId: objectTypeId
    });
  };

  // This used to be buried in the CallSettingsNumber component but needs to be run for NoCalleesPrompt to work properly
  useEffect(() => {
    preloadCallingWidget();
    const callStatusFromWidgetState = getIn(['callStatus'], props.widgetState);
    const shouldSendUpdateReadyState = [READY, UNINITIALIZED, LOADING].includes(callStatusFromWidgetState);
    if (shouldSendUpdateReadyState) {
      updateCallingReadyState({
        subjectId: props.subjectId,
        objectTypeId: props.objectTypeId
      });
    }
  }, [props.objectTypeId, props.widgetState, props.subjectId]);
  const renderNoCalleesMessage = () => {
    const {
      objectTypeId,
      onCallButtonClick,
      subjectId
    } = props;
    return /*#__PURE__*/_jsx(NoCalleesPrompt, {
      subjectId: Number(subjectId),
      objectTypeId: objectTypeId,
      onAddNumberClick: onCallButtonClick,
      shouldShowAddNumber: true
    });
  };
  const renderCallees = () => {
    const {
      appIdentifier,
      onCallButtonClick,
      subjectId,
      objectTypeId,
      asyncCalleeOmnibus,
      asyncSettingsOmnibus,
      widgetState,
      selectedCallProvider,
      selectedFromNumber,
      selectedCallMethod,
      scopes,
      limits,
      onSetCallStatus,
      onSetThirdPartyActive,
      gates
    } = props;
    const calleeOmnibusData = getData(asyncCalleeOmnibus);
    const calleesData = calleeOmnibusData && getCallableObjectAndAssociations(calleeOmnibusData);
    const hasRecords = hasCallableRecords(calleesData);
    const omnibusData = getData(asyncSettingsOmnibus);
    const callingProviders = getCallProvidersFromOmnibusResult(omnibusData);
    const enableBiDirectionalProviderSwitching = getIsUngatedForBidirectionalProviderSwitching(gates, appIdentifier, callingProviders);
    return /*#__PURE__*/_jsx(_Fragment, {
      children: !isSucceeded(asyncCalleeOmnibus) || hasRecords ? /*#__PURE__*/_jsx(Options, {
        className: "flex-column",
        "data-selenium-test": "call-settings-numbers-options",
        children: /*#__PURE__*/_jsx(CallSettingsNumbers, {
          appIdentifier: appIdentifier,
          asyncCalleeOmnibus: asyncCalleeOmnibus,
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          onCallButtonClick: onCallButtonClick,
          subjectId: subjectId,
          objectTypeId: objectTypeId,
          widgetState: widgetState,
          selectedCallProvider: selectedCallProvider,
          selectedFromNumber: selectedFromNumber,
          selectedCallMethod: selectedCallMethod,
          scopes: scopes,
          limits: limits,
          onSetCallStatus: onSetCallStatus,
          onSetThirdPartyActive: onSetThirdPartyActive,
          enableBiDirectionalProviderSwitching: enableBiDirectionalProviderSwitching
        })
      }) : renderNoCalleesMessage()
    });
  };
  const {
    appIdentifier,
    scopes,
    limits,
    gates,
    onCallButtonClick,
    asyncSettingsOmnibus,
    callingAccess
  } = props;
  return /*#__PURE__*/_jsx(Wrapper, {
    children: /*#__PURE__*/_jsx(CalleeErrorBoundary, {
      appName: "CALLING",
      children: /*#__PURE__*/_jsx(EnsureCallingAccess, {
        appIdentifier: appIdentifier,
        useFullWidth: false,
        scopes: scopes,
        callingAccess: callingAccess,
        children: /*#__PURE__*/_jsx(CallingOnboarding, {
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          onRegisterFromPhoneNumber: onCallButtonClick,
          onShowOnboardingIntro: onCallButtonClick,
          onClosePopover: onCallButtonClick,
          scopes: scopes,
          limits: limits,
          gates: gates,
          appIdentifier: appIdentifier,
          children: /*#__PURE__*/_jsx("div", {
            className: "flex-column",
            children: getIsCRMEmbedded() ? renderCRMEmbeddedMessage() : renderCallees()
          })
        })
      })
    })
  });
};
CallNumbersPopover.proptypes = {
  asyncCalleeOmnibus: RecordPropType('AsyncCalleeOmnibusResult').isRequired,
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  appIdentifier: PropTypes.string.isRequired,
  onCallButtonClick: PropTypes.func,
  scopes: ImmutablePropTypes.map.isRequired,
  limits: ImmutablePropTypes.map.isRequired,
  gates: PropTypes.array.isRequired,
  subjectId: PropTypes.string.isRequired,
  objectTypeId: PropTypes.string.isRequired,
  callingAccess: RecordPropType('CallingAccess').isRequired,
  widgetState: ImmutablePropTypes.map.isRequired,
  selectedCallProvider: RecordPropType('CallingProvider'),
  selectedFromNumber: RecordPropType('FromNumber'),
  selectedCallMethod: PropTypes.string,
  onSetCallStatus: PropTypes.func.isRequired,
  onSetThirdPartyActive: PropTypes.func.isRequired
};
CallNumbersPopover.defaultProps = {
  onCallButtonClick: emptyFunction
};
export default compose(withCalleeOmnibus, withCallingAccess, withWidgetState, withAccessLevelPrefetch)(CallNumbersPopover);